<template>
  <div class="support-chat-shell">
    <!-- Floating trigger button -->
    <div class="support-trigger" :class="{ 'is-hidden': chat.isOpen }">
      <button class="trigger-btn" @click="openChatFromTrigger()">
        <span class="trigger-icon">
          <svg v-if="chat.unreadCount === 0" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </span>
        <span v-if="chat.unreadCount > 0" class="trigger-badge">{{ chat.unreadCount }}</span>
      </button>

      <Transition name="prompt-pop">
        <button
          v-if="showPrompt && promptVisible"
          type="button"
          class="support-prompt-callout"
          :aria-label="$t('supportChat.prompt_action')"
          @click="openChatFromPrompt()"
        >
          <span class="support-prompt-icon" aria-hidden="true">
            <i class="fa-solid fa-circle-question"></i>
          </span>
          <span class="support-prompt-copy">
            <strong>{{ $t('supportChat.prompt_title') }}</strong>
            <span>{{ $t('supportChat.prompt_text') }}</span>
          </span>
          <button
            type="button"
            class="support-prompt-close"
            :aria-label="$t('supportChat.dismiss_prompt')"
            @click.stop="dismissPrompt()"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <span class="support-prompt-arrow" aria-hidden="true"></span>
        </button>
      </Transition>
      <div class="trigger-ring"></div>
      <div class="trigger-pulse-ring"></div>
    </div>

    <!-- Chat panel -->
    <Transition name="panel-slide">
      <div v-if="chat.isOpen" class="support-panel" dir="rtl">
        <!-- Header -->
        <div class="panel-header">
          <div class="header-wave"></div>
          <div class="header-content">
            <button class="header-close" @click="chat.close()">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
            <div class="header-info">
              <div class="header-avatar">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div class="header-text">
                <span class="header-title">{{ $t('supportChat.header_title') }}</span>
                <span class="header-subtitle">{{ $t('supportChat.header_subtitle') }}</span>
              </div>
            </div>
            <div class="header-status">
              <span class="status-dot"></span>
              <span>{{ $t('supportChat.online') }}</span>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="panel-body">
          <!-- Form step -->
          <div v-if="chat.step === 'form'" class="form-view">
            <div class="form-intro">
              <div class="intro-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <p>{{ $t('supportChat.form_intro') }}</p>
            </div>

            <div class="form-fields">
              <!-- Name -->
              <div class="field-group" :class="{ 'has-value': chat.form.name }">
                <label class="field-label">{{ $t('supportChat.name_label') }}</label>
                <input
                  v-model="chat.form.name"
                  type="text"
                  class="field-input"
                  :placeholder="$t('supportChat.name_placeholder')"
                  maxlength="60"
                />
              </div>

              <!-- Mobile -->
              <div class="field-group" :class="{ 'has-value': chat.form.mobile }">
                <label class="field-label">{{ $t('supportChat.mobile_label') }}</label>
                <input
                  v-model="chat.form.mobile"
                  type="tel"
                  class="field-input ltr-input"
                  placeholder="09123456789"
                  maxlength="11"
                  dir="ltr"
                />
              </div>

              <!-- Category multi-select with search -->
              <div class="field-group" ref="categoryWrapRef">
                <label class="field-label">{{ $t('supportChat.category_label') }}</label>
                <div class="category-search-wrap">
                  <input
                    v-model="chat.form.categorySearch"
                    type="text"
                    class="field-input"
                    :placeholder="$t('supportChat.category_placeholder')"
                    @input="chat.searchCategories(chat.form.categorySearch)"
                    @focus="chat.onCategorySearchFocus()"
                    @blur="chat.onCategorySearchBlur()"
                  />
                  <Transition name="dropdown-fade">
                    <div v-if="chat.categoryDropdownVisible" class="category-dropdown">
                      <button
                        v-for="(item, idx) in chat.searchResults"
                        :key="idx"
                        class="category-option"
                        :class="{ 'is-selected': chat.form.categories.some(c => c.value === item.value) }"
                        @mousedown.prevent="chat.selectCategory(item)"
                      >
                        <span class="option-check">
                          <svg v-if="chat.form.categories.some(c => c.value === item.value)" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                          </svg>
                        </span>
                        <span>{{ item.label }}</span>
                      </button>
                      <div v-if="chat.searchResults.length === 0 && chat.form.categorySearch.trim().length >= 3" class="dropdown-empty">
                        <span v-if="chat.searchLoading">{{ $t('supportChat.searching') }}</span>
                        <span v-else>{{ $t('supportChat.no_results') }}</span>
                      </div>
                    </div>
                  </Transition>
                </div>
                <!-- Selected categories chips -->
                <div v-if="chat.form.categories.length > 0" class="selected-chips">
                  <span
                    v-for="cat in chat.form.categories"
                    :key="cat.value"
                    class="chip"
                    @click="chat.removeCategory(cat)"
                  >
                    {{ cat.label }}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
                      <path d="M18 6 6 18M6 6l12 12"/>
                    </svg>
                  </span>
                </div>
                <div v-if="chat.form.categorySearch.trim().length >= 3 && chat.form.categories.length === 0" class="raw-text-hint">
                  «{{ chat.form.categorySearch }}» {{ $t('supportChat.free_text_hint') }}
                </div>
              </div>

              <!-- Weight + Payment row -->
              <div class="field-row">
                <div class="field-group field-half" :class="{ 'has-value': chat.form.weight }">
                  <label class="field-label">{{ $t('supportChat.weight_label') }}</label>
                  <div class="weight-wrap">
                    <input
                      v-model="chat.form.weight"
                      type="text"
                      class="field-input"
                      :placeholder="$t('supportChat.weight_placeholder')"
                      inputmode="numeric"
                    />
                    <span class="weight-suffix">{{ $t('supportChat.weight_suffix') }}</span>
                  </div>
                </div>
                
                <div class="field-group field-half" :class="{ 'has-value': chat.form.payment_method }" ref="paymentRef">
                  <label class="field-label">{{ $t('supportChat.payment_label') }}</label>
                  <div class="select-wrap" @click="showPaymentDropdown = !showPaymentDropdown">
                    <div class="select-trigger">
                      <span v-if="chat.form.payment_method">
                        {{ chat.filteredPaymentMethods.find(m => m.value === chat.form.payment_method)?.label }}
                      </span>
                      <span v-else class="select-placeholder">{{ $t('supportChat.select_placeholder') }}</span>
                      <svg class="select-arrow" :class="{ 'is-open': showPaymentDropdown }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                        <path d="m6 9 6 6 6-6"/>
                      </svg>
                    </div>
                    <Transition name="dropdown-fade">
                      <div v-if="showPaymentDropdown" class="payment-dropdown">
                        <button
                          v-for="method in chat.filteredPaymentMethods"
                          :key="method.value"
                          class="payment-option"
                          :class="{ 'is-active': chat.form.payment_method === method.value }"
                          @click="chat.form.payment_method = method.value; showPaymentDropdown = false"
                        >
                          {{ method.label }}
                        </button>
                      </div>
                    </Transition>
                  </div>
                </div>
                
              </div>
              <span class="raw-text-hint">{{ $t('supportChat.raw_hint') }}</span>
            </div>

            <!-- Submit -->
            <button
              class="submit-btn"
              :class="{ 'is-loading': chat.isLoading }"
              :disabled="!chat.isFormValid || chat.isLoading"
              @click="chat.submitForm()"
            >
              <span v-if="!chat.isLoading">{{ $t('supportChat.submit') }}</span>
              <span v-else class="submit-loader">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
              </span>
            </button>
          </div>

          <!-- Chat step -->
          <div v-else class="chat-view">
            <div class="messages-list" ref="messagesRef">
              <div
                v-for="msg in chat.messages"
                :key="msg.id"
                class="message"
                :class="msg.sender === 'user' ? 'msg-user' : 'msg-support'"
              >
                <div class="msg-bubble">
                  <p>{{ msg.message }}</p>
                </div>
                <span class="msg-time">{{ formatTime(msg.timestamp) }}</span>
              </div>
              <div v-if="chat.isSending" class="message msg-user">
                <div class="msg-bubble is-typing">
                  <span class="typing-dot"></span>
                  <span class="typing-dot"></span>
                  <span class="typing-dot"></span>
                </div>
              </div>
            </div>

            <!-- Chat input -->
            <div class="chat-input-bar">
              <button class="input-attach" @click="chat.resetForm()">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 9l5 5 5-5M12 4v10"/>
                </svg>
              </button>
              <input
                v-model="chatInput"
                type="text"
                class="input-message"
                :placeholder="$t('supportChat.message_placeholder')"
                @keydown.enter.prevent="sendMsg"
              />
              <button
                class="input-send"
                :disabled="!chatInput.trim()"
                @click="sendMsg"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { SUPPORT_CHAT_PROMPT_DELAY, SUPPORT_CHAT_PROMPT_VISIBLE_MS, canShowSupportChatPrompt } from '~/composables/supportChatPrompt'
const { t } = useI18n()

const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  showPrompt: {
    type: Boolean,
    default: false
  }
})

const chat = useSupportChat()
const auth = useAuth()

// Pre-fill form fields (name, mobile) from the authenticated user when empty
function prefillFromUser() {
  const user = auth.authenticated.value ? auth.user.value : null
  if (!user) return
  if (!chat.form.name) {
    chat.form.name = user.name || user.username || user.mobile || ''
  }
  if (!chat.form.mobile) {
    chat.form.mobile = user.mobile || ''
  }
}
const promptVisible = ref(false)
const promptDismissed = ref(false)
let promptTimer = null
let hideTimer = null

function clearPromptTimers() {
  if (promptTimer !== null) {
    window.clearTimeout(promptTimer)
    promptTimer = null
  }
  if (hideTimer !== null) {
    window.clearTimeout(hideTimer)
    hideTimer = null
  }
}

function resetPrompt() {
  clearPromptTimers()
  promptVisible.value = false
  promptDismissed.value = false
}

function dismissPrompt() {
  promptDismissed.value = true
  promptVisible.value = false
  clearPromptTimers()
}

function schedulePrompt() {
  clearPromptTimers()
  if (!props.showPrompt || !process.client || chat.isOpen || promptDismissed.value) return

  promptTimer = window.setTimeout(() => {
    promptTimer = null
    if (canShowSupportChatPrompt({
      isOpen: chat.isOpen,
      dismissed: promptDismissed.value,
      delayElapsed: true
    })) {
      promptVisible.value = true
      hideTimer = window.setTimeout(() => {
        hideTimer = null
        dismissPrompt()
      }, SUPPORT_CHAT_PROMPT_VISIBLE_MS)
    }
  }, SUPPORT_CHAT_PROMPT_DELAY)
}

function openChatFromPrompt() {
  dismissPrompt()
  chat.open()
}

function openChatFromTrigger() {
  if (props.showPrompt) dismissPrompt()
  chat.open()
}

watch(() => [props.showPrompt, chat.isOpen], ([showPrompt, isOpen], oldValue) => {
  const wasShowPrompt = oldValue?.[0]
  if (showPrompt && !wasShowPrompt) resetPrompt()
  if (!showPrompt) {
    promptVisible.value = false
    clearPromptTimers()
  } else if (isOpen) {
    promptVisible.value = false
    clearPromptTimers()
  } else {
    schedulePrompt()
  }
}, { immediate: true })
const showPaymentDropdown = ref(false)
const messagesRef = ref(null)
const paymentRef = ref(null)
const categoryWrapRef = ref(null)
const chatInput = ref('')
function updatePageContext() {
  chat.form.last_page_id = props.product?.id || props.product?.invoiceNum || ''
  chat.form.last_visit = process.client ? window.location.href : ''
}
updatePageContext()
prefillFromUser()
watch(() => props.product, (newProduct, oldProduct) => {
  if (newProduct) updatePageContext()
}, { immediate: true })
// Re-fill user details when returning to the form step (e.g. after reset)
watch(() => chat.step, (newStep) => {
  if (newStep === 'form') prefillFromUser()
})
// Refresh auth from cookies when the panel opens, so a login that happened
// after mount (without a page reload) is picked up too.
watch(() => chat.isOpen, (open) => {
  if (!open) return
  auth.initAuth()
  prefillFromUser()
})
function sendMsg() {
  const text = chatInput.value.trim()
  if (!text) return
  chat.sendMessage(text)
  chatInput.value = ''
}

function formatTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
}

function handleDocClick(e) {
  if (paymentRef.value && !paymentRef.value.contains(e.target)) {
    showPaymentDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocClick)
  schedulePrompt()
})
onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocClick)
  clearPromptTimers()
})

watch(() => chat.messages.length, () => {
  nextTick(() => {
    if (messagesRef.value) {
      messagesRef.value.scrollTop = messagesRef.value.scrollHeight
    }
  })
})

defineExpose({
  open: chat.open,
  close: chat.close
})
</script>

<style lang="scss" scoped>
@use "sass:color";

.support-chat-shell {
  position: fixed;
  z-index: 1049;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  font-family: 'IRANYekan', 'Vazirmatn', sans-serif;
}

.support-trigger {
  position: absolute;
  bottom: 80px;
  left: auto;
  right: 16px;
  z-index: 10;
  pointer-events: auto;
  transition: opacity 0.3s, transform 0.3s;

  &.is-hidden {
    opacity: 0;
    transform: scale(0.6);
    pointer-events: none;
  }
}

.trigger-btn {
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #f2643d 0%, #e8a838 100%);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  animation: btn-enter 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards,
             btn-pulse 2s ease-in-out 0.5s infinite;

  &:hover {
    animation: btn-enter 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards,
               btn-pulse-hover 2s ease-in-out 0.5s infinite;
  }

  &:active {
    transform: scale(0.92);
  }
}

@keyframes btn-enter {
  from { opacity: 0; transform: scale(0); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes btn-pulse {
  0%, 100% {
    box-shadow: 0 4px 20px rgba(242, 100, 61, 0.4);
  }
  50% {
    box-shadow: 0 4px 30px rgba(242, 100, 61, 0.7);
  }
}

@keyframes btn-pulse-hover {
  0%, 100% {
    box-shadow: 0 6px 28px rgba(242, 100, 61, 0.5);
  }
  50% {
    box-shadow: 0 6px 36px rgba(242, 100, 61, 0.8);
  }
}

.trigger-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.trigger-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  background: #dc3545;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
  animation: badge-pop 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.trigger-ring {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid rgba(242, 100, 61, 0.2);
  animation: ring-expand 2s ease-in-out infinite;
}

.trigger-pulse-ring {
  position: absolute;
  inset: -12px;
  border-radius: 50%;
  border: 2px solid rgba(242, 100, 61, 0.12);
  animation: ring-expand 2s ease-in-out 0.6s infinite;
}

@keyframes ring-expand {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0; }
}

@keyframes badge-pop {
  0% { transform: scale(0); }
  70% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

/* Details-page prompt */
.support-prompt-callout {
  position: absolute;
  right: calc(100% + 14px);
  bottom: 4px;
  z-index: 12;
  display: flex;
  align-items: center;
  gap: 10px;
  width: min(260px, calc(100vw - 84px));
  min-height: 64px;
  padding: 10px 14px 10px 12px;
  border: 1px solid rgba(3, 160, 84, 0.18);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 30px rgba(36, 53, 43, 0.16);
  color: $ink;
  cursor: pointer;
  direction: rtl;
  text-align: right;
  transform-origin: right center;
  animation: prompt-arrive 0.45s cubic-bezier(0.16, 1, 0.3, 1) backwards;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  &:hover {
    border-color: rgba(3, 160, 84, 0.42);
    box-shadow: 0 14px 34px rgba(36, 53, 43, 0.2);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid rgba(3, 160, 84, 0.28);
    outline-offset: 3px;
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
}

.support-prompt-icon {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, $leaf, $sun);
  color: #fff;
  font-size: 0.95rem;
  box-shadow: 0 5px 12px rgba(3, 160, 84, 0.24);
}

.support-prompt-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  line-height: 1.55;

  strong {
    color: $ink;
    font-size: 0.8rem;
    font-weight: 800;
  }

  span {
    color: $ink-soft;
    font-size: 0.72rem;
  }
}

.support-prompt-arrow {
  position: absolute;
  right: -6px;
  top: 50%;
  width: 12px;
  height: 12px;
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid rgba(3, 160, 84, 0.18);
  border-right: 1px solid rgba(3, 160, 84, 0.18);
  transform: translateY(-50%) rotate(45deg);
}

.support-prompt-close {
  position: absolute;
  top: 6px;
  left: 6px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  color: #888;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.75rem;
  z-index: 2;
  transition: all 0.2s;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(0, 0, 0, 0.12);
    color: #333;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
}

@keyframes prompt-arrive {
  from {
    opacity: 0;
    transform: translateX(10px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

.prompt-pop-enter-active,
.prompt-pop-leave-active {
  transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.prompt-pop-enter-from,
.prompt-pop-leave-to {
  opacity: 0;
  transform: translateX(10px) scale(0.96);
}

/* Responsive */
@media (max-width: 499px) {
  .support-prompt-callout {
    right: 0;
    bottom: calc(100% + 10px);
    width: min(230px, calc(100vw - 32px));
    transform-origin: right bottom;
    animation-name: prompt-arrive-top;
  }

  .support-prompt-arrow {
    right: 28px;
    top: auto;
    bottom: -6px;
    border-top: none;
    border-right: 1px solid rgba(3, 160, 84, 0.18);
    border-bottom: 1px solid rgba(3, 160, 84, 0.18);
    border-left: none;
    transform: translateX(50%) rotate(45deg);
  }

  .prompt-pop-enter-from,
  .prompt-pop-leave-to {
    transform: translateY(8px) scale(0.96);
  }
}

@keyframes prompt-arrive-top {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .support-prompt-callout {
    animation: none;
  }

  .prompt-pop-enter-active,
  .prompt-pop-leave-active {
    transition: none;
  }
}

/* Panel */
.support-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9998;
  pointer-events: auto;
  background: linear-gradient(180deg, #f8f6f3 0%, #fefcf8 100%);
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.12);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  direction: rtl;
}

.panel-header {
  position: relative;
  background: linear-gradient(135deg, #1a6b3c 0%, #03a054 100%);
  color: #fff;
  padding: 16px 16px 14px;
  overflow: hidden;
}

.header-wave {
  position: absolute;
  bottom: -20px;
  left: -10%;
  right: -10%;
  height: 30px;
  background: radial-gradient(ellipse at 50% 100%, rgba(255,255,255,0.12) 0%, transparent 70%);
  border-radius: 50%;
}

.header-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-close {
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover { background: rgba(255,255,255,0.25); }
}

.header-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255,255,255,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.header-title {
  font-size: 0.95rem;
  font-weight: 700;
}

.header-subtitle {
  font-size: 0.72rem;
  opacity: 0.8;
}

.header-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  opacity: 0.85;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 6px rgba(74, 222, 128, 0.6);
  animation: dot-pulse 2s ease-in-out infinite;
}

@keyframes dot-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Body */
.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  min-height: 200px;
  max-height: 60vh;
  overscroll-behavior: contain;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 4px; }
}

/* Form */
.form-view {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-intro {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(3, 160, 84, 0.06);
  border-radius: 12px;
  border: 1px solid rgba(3, 160, 84, 0.1);
}

.intro-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1a6b3c, #03a054);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.form-intro p {
  margin: 0;
  font-size: 0.82rem;
  color: #333;
  line-height: 1.6;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #666;
  padding-right: 4px;
}

.field-half {
  min-width: 0;
}

.field-input {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e0d8cc;
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: inherit;
  background: #fff;
  color: #333;
  transition: all 0.2s;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #03a054;
    box-shadow: 0 0 0 3px rgba(3, 160, 84, 0.1);
  }

  &::placeholder { color: #b0a89c; }
}

.ltr-input { direction: ltr; text-align: left; }

/* Category search */
.category-search-wrap {
  position: relative;
}

.category-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 20;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #e0d8cc;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
  max-height: 180px;
  overflow-y: auto;
}

.category-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: none;
  text-align: right;
  font-size: 0.85rem;
  font-family: inherit;
  color: #333;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: rgba(3, 160, 84, 0.05); }

  &.is-selected { color: #03a054; font-weight: 600; }

  .option-check {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: 1.5px solid #d0c8bc;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #fff;
    transition: all 0.2s;
  }

  &.is-selected .option-check {
    background: #03a054;
    border-color: #03a054;
  }
}

.dropdown-empty {
  padding: 16px;
  text-align: center;
  color: #999;
  font-size: 0.8rem;
}

.selected-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.raw-text-hint {
  margin-top: 6px;
  padding: 6px 10px;
  background: rgba(232, 168, 56, 0.08);
  border: 1px solid rgba(232, 168, 56, 0.18);
  border-radius: 8px;
  font-size: 0.75rem;
  color: #b8860b;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  background: rgba(3, 160, 84, 0.08);
  border: 1px solid rgba(3, 160, 84, 0.15);
  border-radius: 20px;
  font-size: 0.78rem;
  color: #1a6b3c;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(220, 53, 69, 0.08);
    border-color: rgba(220, 53, 69, 0.2);
    color: #dc3545;
  }

  svg { flex-shrink: 0; }
}

/* Weight and payment row */
.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.weight-wrap {
  position: relative;

  .field-input { padding-left: 60px; }
}

.weight-suffix {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: #999;
  pointer-events: none;
}

/* Payment select */
.select-wrap {
  position: relative;
}

.select-trigger {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e0d8cc;
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: inherit;
  background: #fff;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s;
  box-sizing: border-box;

  &:hover { border-color: #c8c0b4; }
}

.select-placeholder { color: #b0a89c; }

.select-arrow {
  transition: transform 0.2s;
  color: #999;

  &.is-open { transform: rotate(180deg); }
}

.payment-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 20;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #e0d8cc;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
  overflow: hidden;
}

.payment-option {
  width: 100%;
  padding: 11px 14px;
  border: none;
  background: none;
  text-align: right;
  font-size: 0.85rem;
  font-family: inherit;
  color: #333;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: rgba(3, 160, 84, 0.05); }

  &.is-active {
    color: #03a054;
    font-weight: 600;
    background: rgba(3, 160, 84, 0.06);
  }
}

/* Submit button */
.submit-btn {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  background: linear-gradient(135deg, #03a054, #028a46);
  color: #fff;
  transition: all 0.25s;
  box-shadow: 0 4px 16px rgba(3, 160, 84, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(3, 160, 84, 0.35);
  }

  &:active:not(:disabled) { transform: translateY(0); }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }

  &.is-loading {
    pointer-events: none;
  }
}

.submit-loader svg {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Chat view */
.chat-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 200px;
}

.messages-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
  min-height: 160px;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.08); border-radius: 3px; }
}

.message {
  display: flex;
  flex-direction: column;
  max-width: 82%;
  animation: msg-enter 0.3s ease;

  &.msg-user {
    align-self: flex-start;
  }

  &.msg-support {
    align-self: flex-end;
  }
}

@keyframes msg-enter {
  from { opacity: 0; transform: translateY(10px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.msg-bubble {
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 0.88rem;
  line-height: 1.65;
  word-break: break-word;

  p { margin: 0; }
}

.msg-user .msg-bubble {
  background: linear-gradient(135deg, #1a6b3c, #03a054);
  color: #fff;
  border-bottom-right-radius: 4px;
}

.msg-support .msg-bubble {
  background: #e8e4dc;
  color: #333;
  border-bottom-left-radius: 4px;
}

.msg-time {
  font-size: 0.65rem;
  color: #aaa;
  margin-top: 3px;
  padding: 0 4px;
}

.msg-user .msg-time { text-align: left; }
.msg-support .msg-time { text-align: right; }

/* Typing indicator */
.is-typing {
  display: flex;
  gap: 4px;
  padding: 14px 20px;
}

.typing-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #888;
  animation: typing-bounce 1.4s ease-in-out infinite;

  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.4s; }
}

@keyframes typing-bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

/* Chat input */
.chat-input-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0 0;
  border-top: 1px solid #e8e4dc;
  margin-top: auto;
}

.input-attach {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.5px solid #e0d8cc;
  background: #fff;
  color: #888;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    border-color: #c8c0b4;
    color: #666;
  }
}

.input-message {
  flex: 1;
  padding: 10px 14px;
  border: 1.5px solid #e0d8cc;
  border-radius: 24px;
  font-size: 0.85rem;
  font-family: inherit;
  background: #fff;
  color: #333;
  outline: none;
  transition: border-color 0.2s;

  &:focus { border-color: #03a054; }
  &::placeholder { color: #b0a89c; }
}

.input-send {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #f2643d, #e8a838);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
  box-shadow: 0 2px 10px rgba(242, 100, 61, 0.3);

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 14px rgba(242, 100, 61, 0.4);
  }

  &:active:not(:disabled) { transform: scale(0.95); }
  &:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
}

/* Panel transition */
.panel-slide-enter-active {
  animation: panel-in 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.panel-slide-leave-active {
  animation: panel-in 0.25s ease-in reverse;
}

@keyframes panel-in {
  from { transform: translateY(100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* Dropdown transitions */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Responsive */
@media (min-width: 500px) {
  .support-panel {
    left: auto;
    right: 16px;
    width: 400px;
    bottom: 80px;
    border-radius: 20px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
    max-height: 620px;
  }

  .support-trigger {
    bottom: 24px;
    left: auto;
    right: 24px;
  }
}

@media (max-width: 499px) {
  .support-panel {
    border-radius: 16px 16px 0 0;
  }

  .panel-body {
    max-height: 65vh;
    padding: 12px;
  }
}
</style>
